import { useState } from "react";
import { View, Text, TouchableOpacity, Alert, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { Run } from "../app/lib/types";
import { formatTime } from "../app/lib/format";

interface RunCardProps {
  run: Run;
  onEdit?: (run: Run) => void;
  onDelete?: (runId: string) => void;
  isPersonalBest?: boolean;
  /** Hide the notes preview line — used on public profiles. */
  hideNotes?: boolean;
  /** Show Advanced Run History (AKC-matched event name + lure operator) —
   *  in-app owner view only. Public profile always omits these. */
  showAdvanced?: boolean;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export function RunCard({ run, onEdit, onDelete, isPersonalBest, hideNotes, showAdvanced }: RunCardProps) {
  const [showActions, setShowActions] = useState(false);
  const speed = parseFloat(run.speedMph);
  const points = parseFloat(run.points);
  const timeSeconds = parseFloat(run.timeSeconds);

  function handleDelete() {
    if (Platform.OS === "web") {
      if (confirm("Delete this run? This cannot be undone.")) {
        onDelete?.(run.id);
      }
    } else {
      Alert.alert("Delete Run", "Delete this run? This cannot be undone.", [
        { text: "Cancel", style: "cancel" },
        { text: "Delete", style: "destructive", onPress: () => onDelete?.(run.id) },
      ]);
    }
  }

  return (
    <TouchableOpacity
      onLongPress={() => setShowActions(!showActions)}
      onPress={() => showActions ? setShowActions(false) : undefined}
      activeOpacity={0.8}
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 12,
        padding: 16,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: showActions ? "#1B2A4A" : "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 3,
        elevation: 1,
      }}
    >
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Text style={{ fontSize: 14, fontWeight: "600", color: "#111827" }}>
              {formatDate(run.date)}
            </Text>
            {(run as any).location ? (
              <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                <Ionicons name="location-outline" size={12} color="#6b7280" />
                <Text style={{ fontSize: 12, color: "#6b7280", fontWeight: "600" }} numberOfLines={1}>
                  {(run as any).location}
                </Text>
              </View>
            ) : null}
            {run.runNumber > 0 && (
              <View style={{ backgroundColor: run.runNumber === 1 ? "#dbeafe" : "#e0e7ff", paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                <Text style={{ fontSize: 10, fontWeight: "600", color: run.runNumber === 1 ? "#1d4ed8" : "#4338ca" }}>
                  Run {run.runNumber}
                </Text>
              </View>
            )}
          </View>
          {/* Advanced Run History — AKC-matched event name + lure operator,
              split on separate lines now that Date + Location share the top row.
              Owner view only; public profile omits via no showAdvanced. */}
          {showAdvanced && (run as any).eventName ? (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4, marginTop: 3 }}>
              <Ionicons name="ribbon-outline" size={12} color="#6b7280" />
              <Text style={{ fontSize: 12, color: "#374151", flex: 1 }} numberOfLines={1}>
                {(run as any).eventName}
              </Text>
            </View>
          ) : null}
          {showAdvanced && (run as any).lureOperator ? (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4, marginTop: 3 }}>
              <Ionicons name="navigate-outline" size={12} color="#6b7280" />
              <Text style={{ fontSize: 12, color: "#374151", flex: 1 }} numberOfLines={1}>
                Lure: {(run as any).lureOperator}
              </Text>
            </View>
          ) : null}
          {run.notes && !hideNotes ? (
            <Text style={{ fontSize: 14, color: "#9ca3af", marginTop: 3 }} numberOfLines={1}>
              {run.notes}
            </Text>
          ) : null}
        </View>

        {(run.titleEarned || isPersonalBest) && (
          <View style={{ marginLeft: 8, flexDirection: "row", alignItems: "center", gap: 4 }}>
            {run.titleEarned && (
              <View
                style={{
                  backgroundColor: "#fef3c7",
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 12,
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: "700", color: "#92400e" }}>
                  🏆 {run.titleEarned}
                </Text>
              </View>
            )}
            {isPersonalBest && (
              <View
                style={{
                  backgroundColor: "#fbbf24",
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 12,
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: "800", color: "#78350f" }}>
                  🏆 PB
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Actions toggle */}
        {(onEdit || onDelete) && (
          <TouchableOpacity
            onPress={() => setShowActions(!showActions)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={{ marginLeft: 8, padding: 2 }}
          >
            <Ionicons name="ellipsis-vertical" size={16} color="#9ca3af" />
          </TouchableOpacity>
        )}
      </View>

      <View style={{ flexDirection: "row", marginTop: 12, gap: 16 }}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Ionicons name="timer-outline" size={14} color="#6b7280" />
          <Text style={{ fontSize: 15, color: "#374151", marginLeft: 4, fontWeight: "500" }}>
            {formatTime(timeSeconds)}s
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Ionicons name="speedometer-outline" size={14} color="#6b7280" />
          <Text style={{ fontSize: 15, color: "#F5A623", marginLeft: 4, fontWeight: "600" }}>
            {speed.toFixed(2)} mph
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Ionicons name="star-outline" size={14} color="#6b7280" />
          <Text style={{ fontSize: 15, color: "#374151", marginLeft: 4, fontWeight: "500" }}>
            {points.toFixed(2)} pts
          </Text>
        </View>
      </View>

      {/* Edit/Delete actions */}
      {showActions && (
        <View style={{ flexDirection: "row", marginTop: 12, gap: 10, borderTopWidth: 1, borderTopColor: "#f3f4f6", paddingTop: 12 }}>
          {onEdit && (
            <TouchableOpacity
              onPress={() => { setShowActions(false); onEdit(run); }}
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F0F2F5",
                paddingVertical: 8,
                borderRadius: 8,
                gap: 6,
              }}
            >
              <Ionicons name="pencil" size={14} color="#1B2A4A" />
              <Text style={{ color: "#1B2A4A", fontWeight: "600", fontSize: 13 }}>Edit</Text>
            </TouchableOpacity>
          )}
          {onDelete && (
            <TouchableOpacity
              onPress={() => { setShowActions(false); handleDelete(); }}
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fef2f2",
                paddingVertical: 8,
                borderRadius: 8,
                gap: 6,
              }}
            >
              <Ionicons name="trash" size={14} color="#dc2626" />
              <Text style={{ color: "#dc2626", fontWeight: "600", fontSize: 13 }}>Delete</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
}
